/**
 * 승인 요청 관리 페이지 (승인자용)
 *
 * V157: 전면 개편
 * - Firestore 직접 조회 → Cloud Function 경유 (getApprovalsForApprover)
 * - V156.2 스타일 UI (단일 테이블 + 필터 + 검색 + 페이지네이션)
 * - 상세 모달에서 승인/반려 처리
 * - KST 초단위 타임스탬프
 *
 * V157.1: UX 개선
 * - 날짜 컬럼 표시 로직 수정 (type별 extractTargetDate 통일)
 * - 체크박스 + 일괄 승인 기능 추가
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
import { usePendingApprovals } from '../../shared/hooks/useApprovalRequests';
import { formatCompOffDelta } from '../../shared/utils/formatCompOff';  // V78.1
import { useApprovalMutations } from '../../shared/hooks/useApprovalMutations';
import {
  ApprovalRequest,
  ApprovalRequestType,
  ApprovalRequestStatus,
} from '../../shared/types/approvalRequest';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Search,
  RefreshCw,
  Eye,
  CheckCircle,
  XCircle,
  CheckSquare,
  Square,
  Loader2,
} from 'lucide-react';
import { getLeaveUnitLabel } from '../../shared/utils/leaveUnitLabel';  // V69
import { formatDateTimeByCompanyCode } from '../../shared/utils/dateUtils';  // V62
import { getApprovalProjectLabel, withApprovalProjectPrefix } from '../../shared/utils/approvalProjectLabel';  // V70.7

// ============================================
// 타입/라벨 매핑
// ============================================

/**
 * V198: BE handler.ts SSoT 기준으로 타입 라벨 동기화
 * V55: 연장근무 취소 타입 추가
 */
const APPROVAL_TYPE_LABELS: Record<ApprovalRequestType, string> = {
  leave: '휴가',
  leaveCancel: '휴가취소',              // V185
  workSchedule: '근로일정(월)',
  workScheduleChange: '근로일정 변경',
  workScheduleBulkChange: '근로일정 일괄수정',  // V66
  attendanceManual: '출퇴근 수기조정',
  attendanceManualCancel: '출퇴근조정 취소',  // V198
  overtime: '연장근무',
  overtimeCancel: '연장근무 취소',
  weekendWork: '휴일근무',
  weekendWorkCancel: '휴일근무 취소',    // V55
  holidayWork: '휴일근무',               // V247: 기존 데이터 하위호환
  holidayWorkCancel: '휴일근무 취소',    // V247: 기존 데이터 하위호환
  transfer: '전배',                      // V198
  leaveOfAbsence: '휴직 신청',          // V66 Phase 4
  leaveOfAbsenceUpdate: '휴직 수정 및 연장', // V66
};

/**
 * V73: 휴가 요청 유형 칼럼에 실제 휴가 종류 표시
 * - leave → requestData.leaveType (연차휴가, 공가 등)
 * - leaveCancel → (leaveTypeName || leaveType) + ' 취소'
 * - 기타 → 기존 APPROVAL_TYPE_LABELS 그대로
 */
function getTypeDisplayLabel(type: ApprovalRequestType, requestData?: any): string {
  if (type === 'leave') {
    return requestData?.leaveType || APPROVAL_TYPE_LABELS.leave;
  }
  if (type === 'leaveCancel') {
    const leaveName = requestData?.leaveTypeName || requestData?.leaveType || '휴가';
    return `${leaveName} 취소`;
  }
  return APPROVAL_TYPE_LABELS[type] || type;
}

/**
 * V50: orgApproved 상태 추가 (조직장 승인 완료, HR 승인 대기)
 */
const STATUS_LABELS: Record<ApprovalRequestStatus, string> = {
  pending: '승인대기',
  orgApproved: '인사팀 승인대기',  // V50: 조직장 승인 완료
  approved: '승인',
  approved_scheduled: '예약대기',
  applied: '적용완료',
  rejected: '반려',
  canceled: '취소',
  revoked: '취소됨',  // V179.1: 승인된 OT 취소
};

const STATUS_STYLES: Record<ApprovalRequestStatus, { bg: string; color: string }> = {
  pending: { bg: '#FEF3C7', color: '#D97706' },
  orgApproved: { bg: '#DBEAFE', color: '#2563EB' },  // V50: 파란색 계열 (HR 대기)
  approved: { bg: '#DCFCE7', color: '#16A34A' },
  approved_scheduled: { bg: '#FFF7ED', color: '#EA580C' },
  applied: { bg: '#DCFCE7', color: '#16A34A' },
  rejected: { bg: '#FEE2E2', color: '#DC2626' },
  canceled: { bg: '#F3F4F6', color: '#6B7280' },
  revoked: { bg: '#F3E8FF', color: '#7C3AED' },  // V179.1: 보라색 계열
};

/**
 * V55.4: cancelStatus 기반 라벨 (cancelRequested=true일 때 사용)
 */
const CANCEL_STATUS_LABELS: Record<string, string> = {
  pending: '취소승인대기',
  orgApproved: '인사팀 취소승인대기',
  cancelled: '취소완료',
  rejected: '취소반려',
};

const CANCEL_STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  pending: { bg: '#FEF3C7', color: '#D97706' },      // 노란색 (대기)
  orgApproved: { bg: '#DBEAFE', color: '#2563EB' },  // 파란색 (HR 대기)
  cancelled: { bg: '#F3F4F6', color: '#6B7280' },    // 회색 (완료)
  rejected: { bg: '#FEE2E2', color: '#DC2626' },     // 빨간색 (반려)
};

/**
 * V55.4: 상태 라벨 및 스타일 결정 (cancelStatus 우선)
 */
function getStatusDisplay(request: ApprovalRequest): { label: string; bg: string; color: string } {
  // cancelRequested=true이고 cancelStatus가 있으면 취소 상태 표시
  if ((request as any).cancelRequested === true || (request as any).cancelStatus) {
    const cancelStatus = (request as any).cancelStatus || 'pending';
    return {
      label: CANCEL_STATUS_LABELS[cancelStatus] || '취소승인대기',
      ...(CANCEL_STATUS_STYLES[cancelStatus] || CANCEL_STATUS_STYLES.pending),
    };
  }
  // 일반 상태 표시
  return {
    label: STATUS_LABELS[request.status] || request.status,
    ...(STATUS_STYLES[request.status] || STATUS_STYLES.pending),
  };
}

// ============================================
// 유틸리티 함수
// ============================================

/**
 * V62: Timestamp를 회사 타임존 기준 문자열로 변환 (초 단위 포함)
 *
 * 정책 (V62-3): 이 timestamp는 "행위자(HR/조직장) 개인 시간"이 아니라
 * "해당 요청이 속한 회사(request.companyCode)의 시간"으로 표시한다.
 * 예: KR HR이 CN 요청을 승인해도 CN 시간으로 표시.
 * 이유: 동일 요청 내 createdAt·approvedAt 등 모든 timestamp의 시간 기준을 통일하기 위함.
 */
function formatTimestampWithSeconds(timestamp: any, companyCode?: string | null): string {
  return formatDateTimeByCompanyCode(timestamp, companyCode, true);
}

/**
 * V157.1: 요청 대상 날짜 추출 (type별 통일 규칙)
 *
 * - workSchedule: requestData.month (YYYY-MM 형식) 그대로 표시
 * - workScheduleChange: requestData.date (YYYY-MM-DD)
 * - attendanceManual: requestData.date (YYYY-MM-DD)
 * - overtime/weekendWork/holidayWork: requestData.date (YYYY-MM-DD)
 * - leave: requestData.date 또는 startDate
 * - 그 외/없으면 '-'
 */
function extractTargetDate(type: ApprovalRequestType, requestData: any): string {
  if (!requestData) return '-';

  switch (type) {
    case 'workSchedule':
      // V157.1: requestData.month가 YYYY-MM 형식으로 저장됨
      if (requestData.month) {
        return requestData.month; // "2024-12" 형태 그대로
      }
      // 하위호환: year + month 조합
      if (requestData.year && requestData.month) {
        return `${requestData.year}-${String(requestData.month).padStart(2, '0')}`;
      }
      return '-';

    case 'workScheduleChange':
      return requestData.date || requestData.targetDate || '-';

    case 'workScheduleBulkChange': {
      // V66: 다중 날짜 → min ~ max 범위
      const dates: string[] = Array.isArray(requestData.dates) ? [...requestData.dates] : [];
      if (dates.length === 0) return requestData.month || '-';
      dates.sort();
      const minD = dates[0];
      const maxD = dates[dates.length - 1];
      return minD === maxD ? minD : `${minD} ~ ${maxD}`;
    }

    case 'attendanceManual':
      return requestData.date || requestData.targetDate || '-';

    case 'overtime':
    case 'weekendWork':
    case 'holidayWork':
      return requestData.date || requestData.targetDate || '-';

    case 'leave':
    case 'leaveCancel': {
      // V66: 다일 휴가는 시작일 ~ 종료일 표기. 단일일/반차/시간단위는 시작일만.
      const sd = requestData.startDate || requestData.date || requestData.targetDate;
      const ed = requestData.endDate;
      if (sd && ed && ed !== sd) return `${sd} ~ ${ed}`;
      return sd || '-';
    }

    case 'attendanceManualCancel':
      // V198: 출퇴근 수기조정 취소 - date 표시
      return requestData.date || requestData.targetDate || '-';

    case 'transfer':
      // V198: 전배 - effectiveDate 표시
      return requestData.effectiveDate || requestData.date || '-';

    default:
      // V198: 알 수 없는 타입 fallback
      return requestData.date || requestData.targetDate || requestData.startDate || '-';
  }
}

/**
 * 요청 내용 추출 (type별)
 */
function extractContent(type: ApprovalRequestType, requestData: any): string {
  if (!requestData) return '-';

  switch (type) {
    case 'leave':
      return [requestData.leaveType, requestData.reason].filter(Boolean).join(' - ') || '-';
    case 'workSchedule':
      return '월간 근로일정 신청';
    case 'workScheduleChange': {
      // V60: BE 실제 필드명에 맞춤 (originalSchedule, changes)
      const origSch = requestData.originalSchedule;
      const chgSch = requestData.changes;
      if (origSch && chgSch) {
        const before = `${origSch.startTime}~${origSch.endTime}`;
        const afterS = chgSch.startTime || origSch.startTime;
        const afterE = chgSch.endTime || origSch.endTime;
        return `${before} → ${afterS}~${afterE}`;
      }
      return requestData.reason || '-';
    }
    case 'workScheduleBulkChange': {
      // V66: 다중 날짜 변경 요약
      const dates: string[] = Array.isArray(requestData.dates) ? requestData.dates : [];
      const cnt = dates.length;
      const ch = requestData.changes || {};
      const after = (ch.startTime && ch.endTime) ? `${ch.startTime}~${ch.endTime}` : '';
      const tpl = requestData.templateName ? `[${requestData.templateName}]` : '';
      const perDateCnt = requestData.perDateChanges
        ? Object.keys(requestData.perDateChanges).length : 0;
      const perDateBadge = perDateCnt > 0 ? `요일별 차등 ${perDateCnt}일` : '';
      const summary = [tpl, after, cnt > 0 ? `${cnt}일` : '', perDateBadge].filter(Boolean).join(' ');
      return [summary, requestData.reason].filter(Boolean).join(' / ') || '-';
    }
    case 'overtime':
    case 'weekendWork':
    case 'holidayWork':
      const timeRange = requestData.startTime && requestData.endTime
        ? `${requestData.startTime}~${requestData.endTime}`
        : '';
      return [timeRange, requestData.reason].filter(Boolean).join(' / ') || '-';
    case 'attendanceManual':
      // V192: attendanceManualType SSoT 사용 (없으면 기존 추론 로직 fallback)
      let adjustType: string;
      if (requestData.attendanceManualType) {
        adjustType = requestData.attendanceManualType === 'checkIn' ? '출근'
          : requestData.attendanceManualType === 'checkOut' ? '퇴근'
          : '출퇴근';
      } else {
        // fallback: requestedCheckInTime/requestedCheckOutTime으로 추론
        const hasCheckIn = !!requestData.requestedCheckInTime;
        const hasCheckOut = !!requestData.requestedCheckOutTime;
        adjustType = hasCheckIn && hasCheckOut ? '출퇴근' : hasCheckIn ? '출근' : '퇴근';
      }
      return [adjustType, requestData.reason].filter(Boolean).join(' / ') || '-';
    case 'leaveCancel':
      // V185: 휴가 취소 요청 - 휴가 유형명 + 기간 표시
      const dateRange = requestData.startDate && requestData.endDate
        ? `${requestData.startDate}~${requestData.endDate}`
        : requestData.startDate || '';
      return `(취소) ${requestData.leaveTypeName || '휴가'} ${dateRange}`.trim() || '-';

    case 'attendanceManualCancel':
      // V198: 출퇴근 수기조정 취소
      return `(취소) ${requestData.reason || '출퇴근 조정 취소'}`;

    case 'transfer':
      // V198: 전배 - 이동 조직 표시
      if (requestData.fromOrgUnitName && requestData.toOrgUnitName) {
        return `${requestData.fromOrgUnitName} → ${requestData.toOrgUnitName}`;
      }
      return requestData.reason || '전배 요청';

    case 'leaveOfAbsence': {
      // V66 Phase 4: 휴직 신청 — 종류 + 기간 + 사유
      // 표시 형식: "육아휴직 (2026.05.10 ~ 2027.05.09) - 사유"
      const typeName = requestData.typeName || '휴직';
      const sd = requestData.startDate ? requestData.startDate.replace(/-/g, '.') : '';
      const ed = requestData.expectedReturnDate ? requestData.expectedReturnDate.replace(/-/g, '.') : '';
      const period = sd && ed ? `(${sd} ~ ${ed})` : (sd ? `(${sd} ~ )` : '');
      const head = period ? `${typeName} ${period}` : typeName;
      return [head, requestData.reason].filter(Boolean).join(' - ') || '-';
    }

    case 'leaveOfAbsenceUpdate': {
      // V66: 휴직 수정 및 연장 — 변경 전 → 변경 후
      const orig = requestData.original || {};
      const upd = requestData.updated || {};
      const typeName = upd.typeName || orig.typeName || '휴직';
      const fmt = (s: any) => (s ? String(s).replace(/-/g, '.') : '');
      const o = `${fmt(orig.startDate) || '?'} ~ ${fmt(orig.expectedReturnDate) || '미정'}`;
      const u = `${fmt(upd.startDate) || '?'} ~ ${fmt(upd.expectedReturnDate) || '미정'}`;
      const head = `${typeName} / ${o} → ${u}`;
      return requestData.changeReason
        ? `${head} / 변경사유: ${requestData.changeReason}`
        : head;
    }

    default:
      // V198: 알 수 없는 타입 fallback
      return requestData.reason || JSON.stringify(requestData).substring(0, 50) || '-';
  }
}

/**
 * 문자열 말줄임
 */
function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
}

// ============================================
// 메인 컴포넌트
// ============================================

export function WorkApprovalsPage() {
  // 현재 연도 기본값
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [statusFilter, setStatusFilter] = useState<ApprovalRequestStatus | 'all'>('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // 상세 모달
  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequest | null>(null);

  // V157.1: 체크박스 선택 상태
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // V157.1: 일괄 승인 처리 상태
  // V159: failedDetails 추가 (에러 메시지 포함)
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [bulkApproveResult, setBulkApproveResult] = useState<{
    success: number;
    skipped: number;
    failed: number;
    failedIds: string[];
    failedDetails: Array<{ id: string; requesterName: string; error: string }>;
  } | null>(null);

  // 데이터 조회 (V157: CF 사용)
  const {
    requests,
    isLoading,
    isLoadingMore,  // V315: loadMore 전용 로딩
    error,
    refetch,
    hasMore,
    loadMore,
  } = usePendingApprovals({
    status: statusFilter === 'all' ? undefined : statusFilter,
    year: selectedYear,
    pageSize: 100, // 클라이언트 페이지네이션용
  });

  // V157.1: 승인 mutation
  // V55.1: 휴가 취소 직접 승인 함수 추가
  // V55.2: approveCancelLeaveDirectRequest 제거 - handler.ts가 cancelRequested 처리
  const { approveRequest, isProcessing } = useApprovalMutations();

  // 연도 옵션 (2020 ~ 현재+1)
  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let y = 2020; y <= currentYear + 1; y++) {
      years.push(y);
    }
    return years.reverse();
  }, [currentYear]);

  // 검색 필터링 (클라이언트)
  const filteredRequests = useMemo(() => {
    if (!requests) return [];
    if (!searchQuery.trim()) return requests;

    const query = searchQuery.toLowerCase();
    return requests.filter((req) => {
      const typeLabel = getTypeDisplayLabel(req.type, req.requestData).toLowerCase();
      const requesterName = req.requesterName?.toLowerCase() || '';
      const targetDate = extractTargetDate(req.type, req.requestData).toLowerCase();
      // V70.7: 목록 표시와 동일하게 [프로젝트] 포함 → 프로젝트명 검색 가능
      const content = withApprovalProjectPrefix(req.type, req.requestData, req.projectName, extractContent(req.type, req.requestData)).toLowerCase();

      return (
        typeLabel.includes(query) ||
        requesterName.includes(query) ||
        targetDate.includes(query) ||
        content.includes(query)
      );
    });
  }, [requests, searchQuery]);

  // 페이지네이션 (클라이언트)
  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(filteredRequests.length / PAGE_SIZE);

  // V315: currentPage가 범위 밖이면 보정 (검색/필터 변경 후 데이터 감소 시)
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedRequests = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredRequests.slice(start, start + PAGE_SIZE);
  }, [filteredRequests, currentPage]);

  // 페이지 변경 시 맨 위로 + 선택 초기화
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSelectedIds(new Set()); // V157.1: 페이지 변경 시 선택 초기화
    window.scrollTo(0, 0);
  };

  // 필터 변경 시 첫 페이지로 + 선택 초기화
  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    setCurrentPage(1);
    setSelectedIds(new Set()); // V157.1: 필터 변경 시 선택 초기화
  };

  const handleStatusChange = (status: ApprovalRequestStatus | 'all') => {
    setStatusFilter(status);
    setCurrentPage(1);
    setSelectedIds(new Set()); // V157.1: 필터 변경 시 선택 초기화
  };

  // V157.1: 개별 체크박스 토글
  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // V157.1: 전체 선택/해제 (현재 페이지 기준)
  const handleToggleSelectAll = useCallback(() => {
    const currentPageIds = paginatedRequests.map((r) => r.id);
    const allSelected = currentPageIds.every((id) => selectedIds.has(id));

    if (allSelected) {
      // 전체 해제
      setSelectedIds((prev) => {
        const next = new Set(prev);
        currentPageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      // 전체 선택
      setSelectedIds((prev) => {
        const next = new Set(prev);
        currentPageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [paginatedRequests, selectedIds]);

  // V157.1: 현재 페이지 전체 선택 여부
  const isAllCurrentPageSelected = useMemo(() => {
    if (paginatedRequests.length === 0) return false;
    return paginatedRequests.every((r) => selectedIds.has(r.id));
  }, [paginatedRequests, selectedIds]);

  // V157.1: 일괄 승인 처리 (정책 A: pending만 승인, 나머지 스킵)
  // V159: 개별 실패가 전체 진행을 막지 않음 + 상세 에러 로깅
  const handleBulkApprove = useCallback(async () => {
    if (selectedIds.size === 0) {
      setBulkApproveResult({ success: 0, skipped: 0, failed: 0, failedIds: [], failedDetails: [] });
      return;
    }

    setIsBulkApproving(true);
    setBulkApproveResult(null);

    const result = {
      success: 0,
      skipped: 0,
      failed: 0,
      failedIds: [] as string[],
      failedDetails: [] as Array<{ id: string; requesterName: string; error: string }>,
    };

    // 선택된 요청들 중 현재 데이터에서 찾기
    const selectedRequests = (requests || []).filter((r) => selectedIds.has(r.id));

    // 순차 처리 (Rate limit 방지)
    for (const req of selectedRequests) {
      // pending이 아니면 스킵
      if (req.status !== 'pending') {
        result.skipped++;
        continue;
      }

      try {
        // V55.2: 모든 승인요청(취소 포함)은 approveRequest로 처리
        // handler.ts가 cancelRequested 플래그를 감지하여 취소 승인 처리
        await approveRequest({
          approvalRequestId: req.id,
          action: 'approve',
          comment: '',
        });
        result.success++;
      } catch (err: any) {
        // V159: 에러 상세 로깅 (self-approval 포함 모든 에러)
        const errorMessage = err.message || '알 수 없는 오류';
        console.error(`[일괄승인] 실패: ${req.id}`, errorMessage);
        result.failed++;
        result.failedIds.push(req.id);
        result.failedDetails.push({
          id: req.id,
          requesterName: req.requesterName || '-',
          error: errorMessage,
        });
      }

      // Rate limit 방지: 각 호출 간 100ms 대기
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    setIsBulkApproving(false);
    setBulkApproveResult(result);
    setSelectedIds(new Set());
    refetch();
  }, [selectedIds, requests, approveRequest, refetch]);

  return (
    <div style={{ padding: '24px' }}>
      {/* 페이지 제목 */}
      <h1
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'var(--color-gray-800)',
          marginBottom: '24px',
        }}
      >
        승인 요청 관리
      </h1>

      {/* V157.1: 일괄 승인 결과 토스트 */}
      {/* V159: 실패 상세 정보 표시 개선 */}
      {bulkApproveResult && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '16px 20px',
            background: bulkApproveResult.failed > 0 ? '#FEF3C7' : '#DCFCE7',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1100,
            maxWidth: '450px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>일괄 승인 완료</div>
              <div style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>
                성공: {bulkApproveResult.success}건
                {bulkApproveResult.skipped > 0 && ` / 스킵: ${bulkApproveResult.skipped}건`}
                {bulkApproveResult.failed > 0 && ` / 실패: ${bulkApproveResult.failed}건`}
              </div>
              {/* V159: 실패 상세 (신청자명 + 에러 메시지) */}
              {bulkApproveResult.failedDetails && bulkApproveResult.failedDetails.length > 0 && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#DC2626' }}>
                  {bulkApproveResult.failedDetails.map((detail, idx) => (
                    <div key={detail.id} style={{ marginBottom: '2px' }}>
                      • {detail.requesterName}: {detail.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setBulkApproveResult(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* 필터/검색 영역 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        {/* 왼쪽: 상태 필터 + 일괄 승인 버튼 */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value as ApprovalRequestStatus | 'all')}
            style={{
              padding: '8px 12px',
              fontSize: '14px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              background: 'white',
              cursor: 'pointer',
            }}
          >
            <option value="pending">승인대기</option>
            <option value="orgApproved">인사팀 승인대기</option>
            <option value="approved">승인</option>
            <option value="rejected">반려</option>
            <option value="canceled">취소</option>
            <option value="revoked">취소됨</option>
            <option value="all">전체</option>
          </select>

          {/* V157.1: 일괄 승인 버튼 */}
          <button
            onClick={handleBulkApprove}
            disabled={selectedIds.size === 0 || isBulkApproving}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              background: selectedIds.size === 0 ? 'var(--color-gray-200)' : '#16A34A',
              color: selectedIds.size === 0 ? 'var(--color-gray-500)' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: selectedIds.size === 0 || isBulkApproving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {isBulkApproving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                처리 중...
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                일괄 승인 {selectedIds.size > 0 && `(${selectedIds.size})`}
              </>
            )}
          </button>
        </div>

        {/* 오른쪽: 연도 필터 + 검색 + 새로고침 */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* 연도 필터 */}
          <select
            value={selectedYear}
            onChange={(e) => handleYearChange(Number(e.target.value))}
            style={{
              padding: '8px 12px',
              fontSize: '14px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              background: 'white',
              cursor: 'pointer',
            }}
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}년
              </option>
            ))}
          </select>

          {/* 검색창 */}
          <div style={{ position: 'relative' }}>
            <Search
              size={16}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-gray-400)',
              }}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="유형, 신청자, 날짜, 내용 검색"
              style={{
                padding: '8px 12px 8px 32px',
                fontSize: '14px',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '6px',
                width: '220px',
              }}
            />
          </div>

          {/* 새로고침 */}
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            style={{
              padding: '8px',
              background: 'white',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="새로고침"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* 테이블 */}
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {/* 테이블 헤더 - V157.1: 체크박스 열 추가 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '40px 140px 70px 80px 130px 150px 100px 150px 90px minmax(100px, 1fr) 70px',
            padding: '12px 16px',
            background: 'var(--color-gray-50)',
            borderBottom: '1px solid var(--color-gray-200)',
            fontWeight: '600',
            fontSize: '13px',
            color: 'var(--color-gray-600)',
            alignItems: 'center',
          }}
        >
          {/* V157.1: 전체선택 체크박스 */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={handleToggleSelectAll}
              disabled={paginatedRequests.length === 0}
              style={{
                background: 'none',
                border: 'none',
                cursor: paginatedRequests.length === 0 ? 'not-allowed' : 'pointer',
                padding: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isAllCurrentPageSelected ? '#16A34A' : 'var(--color-gray-400)',
              }}
              title={isAllCurrentPageSelected ? '전체 해제' : '전체 선택'}
            >
              {isAllCurrentPageSelected ? <CheckSquare size={18} /> : <Square size={18} />}
            </button>
          </div>
          <div>유형</div>
          <div>사용 단위</div>
          <div>신청자</div>
          <div>날짜</div>
          <div>요청일시</div>
          <div>승인상태</div>
          <div>승인일시</div>
          <div>승인자</div>
          <div>내용</div>
          <div style={{ textAlign: 'center' }}>작업</div>
        </div>

        {/* 테이블 본문 */}
        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
            로딩 중...
          </div>
        ) : error ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-red-500)' }}>
            오류: {error.message}
          </div>
        ) : paginatedRequests.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
            {searchQuery ? '검색 결과가 없습니다.' : '승인 요청이 없습니다.'}
          </div>
        ) : (
          paginatedRequests.map((request, index) => (
            <ApprovalRequestRow
              key={request.id}
              request={request}
              isLast={index === paginatedRequests.length - 1}
              onViewDetail={() => setSelectedRequest(request)}
              isSelected={selectedIds.has(request.id)}
              onToggleSelect={() => handleToggleSelect(request.id)}
            />
          ))
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            marginTop: '20px',
          }}
        >
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '6px 10px',
              background: 'white',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '4px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              opacity: currentPage === 1 ? 0.5 : 1,
            }}
          >
            <ChevronLeft size={16} />
          </button>

          {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 10) {
              pageNum = i + 1;
            } else if (currentPage <= 5) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 4) {
              pageNum = totalPages - 9 + i;
            } else {
              pageNum = currentPage - 4 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                style={{
                  padding: '6px 12px',
                  background: currentPage === pageNum ? 'var(--color-primary)' : 'white',
                  color: currentPage === pageNum ? 'white' : 'var(--color-gray-700)',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: currentPage === pageNum ? '600' : '400',
                }}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '6px 10px',
              background: 'white',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '4px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              opacity: currentPage === totalPages ? 0.5 : 1,
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* V315: 서버에 더 많은 데이터가 있으면 "더 보기" 표시 */}
      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <button
            onClick={loadMore}
            disabled={isLoadingMore}
            style={{
              padding: '8px 24px',
              fontSize: '14px',
              color: isLoadingMore ? 'var(--color-gray-400)' : 'var(--color-primary)',
              backgroundColor: 'white',
              border: `1px solid ${isLoadingMore ? 'var(--color-gray-300)' : 'var(--color-primary)'}`,
              borderRadius: '6px',
              cursor: isLoadingMore ? 'not-allowed' : 'pointer',
            }}
          >
            {isLoadingMore ? '불러오는 중...' : '더 보기'}
          </button>
        </div>
      )}

      {/* 결과 요약 */}
      <div
        style={{
          textAlign: 'center',
          marginTop: '12px',
          fontSize: '13px',
          color: 'var(--color-gray-500)',
        }}
      >
        총 {filteredRequests.length}건
        {searchQuery && ` (검색: "${searchQuery}")`}
      </div>

      {/* 상세/승인/반려 모달 */}
      {selectedRequest && (
        <ApprovalDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onActionComplete={() => {
            setSelectedRequest(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

// ============================================
// 테이블 행 컴포넌트
// ============================================

interface ApprovalRequestRowProps {
  request: ApprovalRequest;
  isLast: boolean;
  onViewDetail: () => void;
  // V157.1: 체크박스 props
  isSelected: boolean;
  onToggleSelect: () => void;
}

function ApprovalRequestRow({
  request,
  isLast,
  onViewDetail,
  isSelected,
  onToggleSelect,
}: ApprovalRequestRowProps) {
  // V55.4: cancelStatus 우선 표시
  const statusDisplay = getStatusDisplay(request);
  const targetDate = extractTargetDate(request.type, request.requestData);
  // V294: 취소 요청 상태이면 취소 사유를 우선 표시
  // V70.7: 연장/휴일근무는 내용 앞에 [프로젝트] 표시
  const content = request.cancelRequested && request.cancelReason
    ? `(취소) ${request.cancelReason}`
    : withApprovalProjectPrefix(request.type, request.requestData, request.projectName, extractContent(request.type, request.requestData));

  // 승인/반려 일시
  const actionTimestamp = request.approvedAt || request.rejectedAt;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '40px 140px 70px 80px 130px 150px 100px 150px 90px minmax(100px, 1fr) 70px',
        padding: '12px 16px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-gray-100)',
        fontSize: '13px',
        color: 'var(--color-gray-700)',
        alignItems: 'center',
        background: isSelected ? 'rgba(22, 163, 74, 0.05)' : 'transparent',
      }}
    >
      {/* V157.1: 체크박스 */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button
          onClick={onToggleSelect}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isSelected ? '#16A34A' : 'var(--color-gray-400)',
          }}
        >
          {isSelected ? <CheckSquare size={18} /> : <Square size={18} />}
        </button>
      </div>

      {/* 유형 - V55.2: cancelRequested 표시 */}
      <div style={{ fontWeight: '500' }}>
        {getTypeDisplayLabel(request.type, request.requestData)}
        {(request as any).cancelRequested === true && (
          <span style={{ color: '#DC2626', marginLeft: '4px' }}>(취소)</span>
        )}
      </div>

      {/* V69: 사용 단위 — 휴가/휴가취소만 표시 */}
      <div style={{ fontSize: '12px' }}>
        {(request.type === 'leave' || request.type === 'leaveCancel')
          ? getLeaveUnitLabel(request.requestData?.unit)
          : '-'}
      </div>

      {/* 신청자 */}
      <div>{request.requesterName || '-'}</div>

      {/* 날짜 */}
      <div>{targetDate}</div>

      {/* 요청일시 */}
      <div style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>
        {formatTimestampWithSeconds(request.createdAt, request.companyCode)}
      </div>

      {/* 승인상태 - V55.4: cancelStatus 우선 표시 */}
      <div>
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: '500',
            background: statusDisplay.bg,
            color: statusDisplay.color,
          }}
        >
          {statusDisplay.label}
        </span>
      </div>

      {/* 승인일시 */}
      <div style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>
        {formatTimestampWithSeconds(actionTimestamp, request.companyCode)}
      </div>

      {/* 승인자 */}
      <div style={{ fontSize: '12px' }}>
        {request.approverName || '-'}
      </div>

      {/* 내용 */}
      {(() => {
        // V66: 근로일정(월) 신청 + 템플릿 사용 시 템플릿명 빨간색 표시
        // - 취소 요청 상태(content가 (취소) ... 로 시작)일 때는 표시하지 않음
        // - 템플릿 미사용 요청은 기존 동작 유지 (templateName === undefined)
        const tplName: string | undefined =
          !request.cancelRequested &&
          request.type === 'workSchedule' &&
          (request.requestData as any)?.templateName
            ? String((request.requestData as any).templateName)
            : undefined;
        const titleText = tplName ? `${content}  ${tplName}` : content;
        return (
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={titleText}
          >
            {truncate(content, 30)}
            {tplName && (
              <span style={{ color: '#dc2626', marginLeft: 8 }}>{tplName}</span>
            )}
          </div>
        );
      })()}

      {/* 작업: 상세 버튼 */}
      <div style={{ textAlign: 'center' }}>
        <button
          onClick={onViewDetail}
          style={{
            padding: '4px 8px',
            background: 'var(--color-gray-100)',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '12px',
            color: 'var(--color-gray-600)',
          }}
        >
          <Eye size={14} />
          상세
        </button>
      </div>
    </div>
  );
}

// ============================================
// 상세/승인/반려 모달
// ============================================

interface ApprovalDetailModalProps {
  request: ApprovalRequest;
  onClose: () => void;
  onActionComplete: () => void;
}

function ApprovalDetailModal({ request, onClose, onActionComplete }: ApprovalDetailModalProps) {
  const { approveRequest, isProcessing } = useApprovalMutations();
  const [comment, setComment] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  // V247: 취소 요청 여부 확인 — 대소문자 안전 판별
  const _t = String(request.type || '');
  const _isCancelType = _t.toLowerCase().endsWith('cancel');
  const isCancelRequest = (request as any).cancelRequested === true || _isCancelType;

  const handleApprove = async () => {
    try {
      setActionError(null);
      // V55.2: 모든 승인요청(취소 포함)은 approveRequest로 처리
      // handler.ts가 cancelRequested 플래그를 감지하여 취소 승인 처리
      await approveRequest({
        approvalRequestId: request.id,
        action: 'approve',
        comment,
      });
      onActionComplete();
    } catch (err: any) {
      setActionError(err.message || '승인 처리 중 오류가 발생했습니다.');
    }
  };

  const handleReject = async () => {
    if (!comment.trim()) {
      setActionError('반려 사유를 입력해주세요.');
      return;
    }

    try {
      setActionError(null);
      // V55.2: 모든 반려요청(취소 포함)은 approveRequest로 처리
      await approveRequest({
        approvalRequestId: request.id,
        action: 'reject',
        comment,
      });
      onActionComplete();
    } catch (err: any) {
      setActionError(err.message || '반려 처리 중 오류가 발생했습니다.');
    }
  };

  // V55.4: cancelStatus 우선 표시
  const statusDisplay = getStatusDisplay(request);
  const isPending = request.status === 'pending';

  return (
    <ModalOverlay
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClose={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
              {getTypeDisplayLabel(request.type, request.requestData)}
              {/* V55.2: 취소 요청 표시 */}
              {isCancelRequest && (
                <span style={{ color: '#DC2626', marginLeft: '8px' }}>(취소)</span>
              )}
            </h2>
            <span
              style={{
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                background: statusDisplay.bg,
                color: statusDisplay.color,
              }}
            >
              {statusDisplay.label}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 본문 */}
        <div style={{ padding: '24px' }}>
          {/* 요청자 정보 */}
          <div style={{ marginBottom: '20px' }}>
            <h3
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--color-gray-600)',
                marginBottom: '8px',
              }}
            >
              요청자 정보
            </h3>
            <div
              style={{
                background: 'var(--color-gray-50)',
                padding: '12px 16px',
                borderRadius: '8px',
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                {request.requesterName}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                {request.requesterOrgUnitName}
              </div>
            </div>
          </div>

          {/* 요청 상세 */}
          <div style={{ marginBottom: '20px' }}>
            <h3
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--color-gray-600)',
                marginBottom: '8px',
              }}
            >
              요청 상세
            </h3>
            <div
              style={{
                background: 'var(--color-gray-50)',
                padding: '16px',
                borderRadius: '8px',
              }}
            >
              <RequestDataDisplay type={request.type} data={request.requestData} cancelReason={request.cancelRequested ? request.cancelReason : undefined} projectName={request.projectName} />
            </div>
          </div>

          {/* 일시 정보 */}
          <div style={{ marginBottom: '20px' }}>
            <h3
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--color-gray-600)',
                marginBottom: '8px',
              }}
            >
              처리 정보
            </h3>
            <div
              style={{
                background: 'var(--color-gray-50)',
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '13px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'var(--color-gray-500)' }}>요청일시</span>
                <span>{formatTimestampWithSeconds(request.createdAt, request.companyCode)}</span>
              </div>
              {request.approvedAt && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: 'var(--color-gray-500)' }}>승인일시</span>
                  <span>{formatTimestampWithSeconds(request.approvedAt, request.companyCode)}</span>
                </div>
              )}
              {request.rejectedAt && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: 'var(--color-gray-500)' }}>반려일시</span>
                  <span>{formatTimestampWithSeconds(request.rejectedAt, request.companyCode)}</span>
                </div>
              )}
              {request.comment && (
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--color-gray-200)' }}>
                  <div style={{ color: 'var(--color-gray-500)', marginBottom: '4px' }}>코멘트</div>
                  <div style={{ color: 'var(--color-gray-700)' }}>{request.comment}</div>
                </div>
              )}
            </div>
          </div>

          {/* V179.1: revoked 상태 표시 */}
          {request.status === 'revoked' && request.revokedAt && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#7C3AED', marginBottom: '8px' }}>
                승인 취소됨
              </h3>
              <div style={{ background: '#F3E8FF', padding: '12px 16px', borderRadius: '8px', fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: 'var(--color-gray-500)' }}>취소일시</span>
                  <span>{formatTimestampWithSeconds(request.revokedAt, request.companyCode)}</span>
                </div>
                {request.revokeReason && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: 'var(--color-gray-500)' }}>취소사유</span>
                    <span>{request.revokeReason}</span>
                  </div>
                )}
                {request.rollbackResult && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--color-gray-500)' }}>대체휴무 조정</span>
                    <span>
                      {request.rollbackResult.deltaDays}일 차감
                      {request.rollbackResult.markNotFound && ' (마킹 없음)'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* V179.1: modifyHistory 표시 */}
          {request.modifyHistory && request.modifyHistory.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-gray-600)', marginBottom: '8px' }}>
                수정 이력 ({request.modifyHistory.length}건)
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {request.modifyHistory.map((entry, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: '#EFF6FF',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      fontSize: '13px',
                    }}
                  >
                    <div style={{ color: 'var(--color-gray-600)', marginBottom: '4px' }}>
                      {entry.at?.split('T')[0] || '-'} 수정
                    </div>
                    <div style={{ color: 'var(--color-gray-800)' }}>
                      {entry.oldStartTime}~{entry.oldEndTime} → {entry.newStartTime}~{entry.newEndTime}
                      {entry.newEndDateOffset === 1 && ' (익일)'}
                    </div>
                    <div style={{ color: 'var(--color-gray-600)', marginTop: '4px' }}>
                      {entry.oldApprovedMinutes}분 → {entry.newApprovedMinutes}분
                      {entry.adjustResult && (
                        <span style={{ marginLeft: '8px', color: '#2563EB' }}>
                          (대체휴무 {formatCompOffDelta(entry.adjustResult.daysDelta)})
                        </span>
                      )}
                    </div>
                    {entry.reason && (
                      <div style={{ color: 'var(--color-gray-500)', marginTop: '4px', fontStyle: 'italic' }}>
                        "{entry.reason}"
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 승인/반려 액션 (pending 상태일 때만) */}
          {isPending && (
            <div style={{ marginTop: '24px' }}>
              <h3
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--color-gray-600)',
                  marginBottom: '8px',
                }}
              >
                {/* V247: 취소요청은 승인만 가능 */}
                {isCancelRequest ? '취소 승인 처리' : '승인/반려 처리'}
              </h3>

              {/* V247: 취소요청은 반려 불가 안내 */}
              {isCancelRequest && (
                <div
                  style={{
                    padding: '8px 12px',
                    background: '#FEF3C7',
                    color: '#92400E',
                    borderRadius: '6px',
                    fontSize: '13px',
                    marginBottom: '12px',
                  }}
                >
                  취소 요청은 반려할 수 없습니다. 승인만 가능합니다.
                </div>
              )}

              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={isCancelRequest ? '코멘트 (선택)' : '코멘트 (반려 시 필수)'}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  resize: 'vertical',
                  minHeight: '80px',
                }}
              />

              {actionError && (
                <div
                  style={{
                    padding: '10px 12px',
                    background: '#FEE2E2',
                    color: '#DC2626',
                    borderRadius: '6px',
                    fontSize: '13px',
                    marginBottom: '12px',
                  }}
                >
                  {actionError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleApprove}
                  disabled={isProcessing}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#16A34A',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  <CheckCircle size={18} />
                  {isCancelRequest ? '취소 승인' : '승인'}
                </button>
                {/* V247: 취소요청은 반려 버튼 숨김 */}
                {!isCancelRequest && (
                  <button
                    onClick={handleReject}
                    disabled={isProcessing}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#DC2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: isProcessing ? 'not-allowed' : 'pointer',
                      opacity: isProcessing ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <XCircle size={18} />
                    반려
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// 요청 데이터 표시 컴포넌트
// ============================================

function RequestDataDisplay({ type, data, cancelReason, projectName }: { type: ApprovalRequestType; data: any; cancelReason?: string; projectName?: string | null }) {
  if (!data) return <div style={{ color: 'var(--color-gray-400)' }}>-</div>;

  const renderRow = (label: string, value: any) => (
    <div style={{ display: 'flex', marginBottom: '8px' }}>
      <div style={{ width: '100px', color: 'var(--color-gray-500)', fontSize: '13px' }}>{label}</div>
      <div style={{ flex: 1, fontSize: '14px' }}>{value || '-'}</div>
    </div>
  );

  // V294.1: 취소 요청 문맥 → 메인 사유란 = 취소사유(B), 신청사유(A)는 보조 행
  const renderReasonRows = () => cancelReason
    ? <>{renderRow('취소사유', cancelReason)}{data.reason && renderRow('신청사유', data.reason)}</>
    : <>{renderRow('사유', data.reason)}</>;

  switch (type) {
    case 'workSchedule':
      // V55.52.2: BE 실제 구조에 맞춤 (month: "YYYY-MM", base: { startTime, ... })
      const [wsYear, wsMonth] = (data.month || '').split('-');
      const baseInfo = data.base;
      return (
        <div>
          {renderRow('기간', wsYear && wsMonth ? `${wsYear}년 ${parseInt(wsMonth, 10)}월` : '-')}
          {baseInfo && renderRow('출근', baseInfo.startTime)}
          {baseInfo && renderRow('점심', `${baseInfo.lunchStart} ~ ${baseInfo.lunchEnd}`)}
          {baseInfo && renderRow('퇴근', baseInfo.endTime)}
        </div>
      );

    case 'workScheduleChange': {
      // V60: BE 실제 구조에 맞춤 (originalSchedule: DailySchedule, changes: Partial<DailySchedule>)
      const orig = data.originalSchedule;
      const chg = data.changes;
      const beforeStr = orig ? `${orig.startTime} ~ ${orig.endTime}` : null;
      // 변경 후: originalSchedule에 changes를 overlay
      const afterStartTime = chg?.startTime || orig?.startTime;
      const afterEndTime = chg?.endTime || orig?.endTime;
      const afterStr = afterStartTime && afterEndTime ? `${afterStartTime} ~ ${afterEndTime}` : null;
      return (
        <div>
          {renderRow('날짜', data.date)}
          {renderRow('변경 전', beforeStr)}
          {renderRow('변경 후', afterStr)}
          {renderReasonRows()}
        </div>
      );
    }

    case 'workScheduleBulkChange': {
      // V66: 다중 날짜 일괄 변경
      const dates: string[] = Array.isArray(data.dates) ? [...data.dates].sort() : [];
      const minD = dates[0];
      const maxD = dates[dates.length - 1];
      const periodStr = dates.length === 0 ? (data.month || '-')
        : minD === maxD ? minD
        : `${minD} ~ ${maxD}`;
      const datesPreview = dates.length === 0 ? '-'
        : dates.length <= 5 ? dates.join(', ')
        : `${dates.slice(0, 5).join(', ')} 외 ${dates.length - 5}건 (총 ${dates.length}일)`;

      // 변경 후 요약 (changes overlay 정책)
      const ch = data.changes || {};
      const afterStart = ch.startTime || '-';
      const afterEnd = ch.endTime || '-';
      const afterLunch = (ch.lunchStart || ch.lunchEnd)
        ? `${ch.lunchStart || '-'} ~ ${ch.lunchEnd || '-'}`
        : null;
      const afterStr = `${afterStart} ~ ${afterEnd}`;

      // 변경 전 요약: originalSchedules 첫 1건만 (대표) + 다양하면 안내
      const orig = data.originalSchedules || {};
      const origVals = Object.values(orig) as Array<any>;
      let beforeStr: string | null = null;
      if (origVals.length > 0) {
        const sample = origVals[0];
        const sampleStr = `${sample?.startTime || '-'} ~ ${sample?.endTime || '-'}`;
        const allSame = origVals.every(v => v?.startTime === sample?.startTime && v?.endTime === sample?.endTime);
        beforeStr = allSame ? sampleStr : `${sampleStr} 외 (날짜별 상이)`;
      }

      // V66c (weekday templates + off sentinel): perDateChanges 그룹 표시
      const perDate = data.perDateChanges && typeof data.perDateChanges === 'object'
        ? data.perDateChanges as Record<string, { startTime: string; lunchStart: string; lunchEnd: string; endTime: string }>
        : null;
      const perDateEntries = perDate ? Object.entries(perDate) : [];
      const isOffSentinel = (s: any) =>
        s.startTime === '' && s.endTime === '' && s.lunchStart === '' && s.lunchEnd === '';
      const groupKey = (s: any) => isOffSentinel(s) ? '(휴무)' : `${s.startTime}~${s.endTime}`;
      const groups = new Map<string, string[]>();
      for (const [d, s] of perDateEntries) {
        const key = groupKey(s);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(d);
      }
      const perDateSummary = Array.from(groups.entries())
        .sort((a, b) => b[1].length - a[1].length)
        .map(([range, ds]) => `${range} (${ds.length}일)`)
        .join(', ');

      return (
        <div>
          {renderRow('대상 기간', periodStr)}
          {renderRow('변경 날짜', datesPreview)}
          {beforeStr && renderRow('변경 전', beforeStr)}
          {renderRow('변경 후 (기본)', afterStr)}
          {afterLunch && renderRow('점심 (변경)', afterLunch)}
          {perDateEntries.length > 0 && renderRow('요일별 차등', `${perDateEntries.length}일에 별도 적용 — ${perDateSummary}`)}
          {data.templateName && renderRow('템플릿', data.templateName)}
          {renderReasonRows()}
        </div>
      );
    }

    case 'attendanceManual': {
      // V192: attendanceManualType SSoT 사용 (없으면 기존 추론 로직 fallback)
      const manualTypeLabel = data.attendanceManualType
        ? (data.attendanceManualType === 'checkIn' ? '출근'
          : data.attendanceManualType === 'checkOut' ? '퇴근'
          : '출퇴근')
        : (data.requestedCheckInTime && data.requestedCheckOutTime ? '출퇴근'
          : data.requestedCheckInTime ? '출근' : '퇴근');

      // V62: HH:mm 포맷 헬퍼 (레거시 ISO 문자열 호환)
      const fmtTime = (v: string | undefined | null): string => {
        if (!v) return '미입력';
        if (typeof v === 'string' && v.includes('T')) {
          return new Date(v).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Seoul' });
        }
        return v; // 이미 HH:mm
      };

      // V62: 수기조정 변경 전/후 표시
      // V66: 익일 퇴근(checkOutDateOffset=1)이면 퇴근 시각 앞에 "익일" 라벨
      const manualType = data.attendanceManualType || (data.requestedCheckInTime && data.requestedCheckOutTime ? 'both' : data.requestedCheckInTime ? 'checkIn' : 'checkOut');
      const isCheckOutNextDay = data.checkOutDateOffset === 1;
      const fmtCheckOut = (v: string | undefined | null): string => {
        const base = fmtTime(v);
        if (!v || base === '미입력') return base;
        return isCheckOutNextDay ? `익일 ${base}` : base;
      };
      let adjustmentDisplay: string;
      if (manualType === 'both') {
        const beforeIn = fmtTime(data.currentCheckInTime);
        const beforeOut = fmtTime(data.currentCheckOutTime);
        const afterIn = fmtTime(data.requestedCheckInTime);
        const afterOut = fmtCheckOut(data.requestedCheckOutTime);
        adjustmentDisplay = `${beforeIn} ~ ${beforeOut}  →  ${afterIn} ~ ${afterOut}`;
      } else if (manualType === 'checkIn') {
        adjustmentDisplay = `${fmtTime(data.currentCheckInTime)}  →  ${fmtTime(data.requestedCheckInTime)}`;
      } else {
        adjustmentDisplay = `${fmtTime(data.currentCheckOutTime)}  →  ${fmtCheckOut(data.requestedCheckOutTime)}`;
      }

      return (
        <div>
          {renderRow('날짜', data.targetDate || data.date)}
          {renderRow('구분', manualTypeLabel)}
          {renderRow('수기조정', adjustmentDisplay)}
          {renderReasonRows()}
        </div>
      );
    }

    case 'overtime':
    case 'weekendWork':
    case 'holidayWork':
      return (
        <div>
          {renderRow('날짜', data.targetDate || data.date)}
          {/* V70.7: 신청자가 선택한 프로젝트 (공통업무 / 프로젝트명 / 삭제된 프로젝트) */}
          {renderRow('프로젝트', getApprovalProjectLabel(data, projectName))}
          {renderRow('시작', data.startTime)}
          {renderRow('종료', data.endTime)}
          {renderReasonRows()}
        </div>
      );

    case 'leave':
      return (
        <div>
          {renderRow('휴가유형', data.leaveType)}
          {renderRow('사용 단위', data.unit === 'hourly' && (data.workHours || data.hours)
            ? `시간단위 (${data.workHours || data.hours}시간${data.startTime && data.endTime ? ` ${data.startTime}~${data.endTime}` : ''})`
            : getLeaveUnitLabel(data.unit))}
          {renderRow('날짜', data.targetDate || data.startDate)}
          {data.endDate && data.endDate !== data.startDate && renderRow('종료', data.endDate)}
          {renderReasonRows()}
          {Array.isArray(data.attachments) && data.attachments.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <div style={{ color: 'var(--color-gray-500)', fontSize: '13px', marginBottom: '6px' }}>첨부파일</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {data.attachments.map((url: string, i: number) => {
                  const isImage = /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url);
                  return (
                    <div key={i}>
                      {isImage && (
                        <img
                          src={url}
                          alt={`첨부 ${i + 1}`}
                          style={{ maxWidth: '240px', maxHeight: '180px', borderRadius: '6px', marginBottom: '4px', cursor: 'pointer' }}
                          onClick={() => window.open(url, '_blank')}
                        />
                      )}
                      <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'var(--color-blue-600)' }}>
                        {isImage ? `이미지 ${i + 1}` : `첨부파일 ${i + 1}`}
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );

    case 'leaveCancel':
      return (
        <div>
          {renderRow('휴가유형', data.leaveTypeName || data.leaveType)}
          {renderRow('사용 단위', data.unit === 'hourly' && (data.workHours || data.hours)
            ? `시간단위 (${data.workHours || data.hours}시간${data.startTime && data.endTime ? ` ${data.startTime}~${data.endTime}` : ''})`
            : getLeaveUnitLabel(data.unit))}
          {renderRow('기간', data.startDate && data.endDate ? `${data.startDate} ~ ${data.endDate}` : data.startDate || '-')}
          {renderReasonRows()}
        </div>
      );

    case 'transfer':
      return (
        <div>
          {data.fromOrgUnitName && data.toOrgUnitName && renderRow('이동', `${data.fromOrgUnitName} → ${data.toOrgUnitName}`)}
          {renderRow('시행일', data.effectiveDate || data.date || '-')}
          {renderReasonRows()}
        </div>
      );

    case 'leaveOfAbsence': {
      // V66 Phase 4: 휴직 신청 상세 — 종류 / 기간 / 사유
      const typeName = data.typeName || '휴직';
      const sd = data.startDate ? data.startDate.replace(/-/g, '.') : '';
      const ed = data.expectedReturnDate ? data.expectedReturnDate.replace(/-/g, '.') : '';
      const period = sd && ed ? `${sd} ~ ${ed}` : (sd || '-');
      return (
        <div>
          {renderRow('휴직 종류', typeName)}
          {renderRow('휴직 기간', period)}
          {renderReasonRows()}
        </div>
      );
    }

    case 'leaveOfAbsenceUpdate': {
      // V66: 휴직 수정 및 연장 — 변경 전 / 변경 후 두 그룹
      const orig = data.original || {};
      const upd = data.updated || {};
      const fmt = (s: any) => (s ? String(s).replace(/-/g, '.') : '-');
      const origPeriod = `${fmt(orig.startDate)} ~ ${fmt(orig.expectedReturnDate)}`;
      const updPeriod = `${fmt(upd.startDate)} ~ ${fmt(upd.expectedReturnDate)}`;
      return (
        <div>
          {renderRow('휴직 종류', upd.typeName || orig.typeName || '휴직')}
          <div style={{ marginTop: 8, marginBottom: 4, fontWeight: 600, color: 'var(--color-gray-700)', fontSize: 13 }}>변경 전</div>
          {renderRow('기간', origPeriod)}
          {renderRow('사유', orig.reason || '-')}
          <div style={{ marginTop: 8, marginBottom: 4, fontWeight: 600, color: 'var(--color-gray-700)', fontSize: 13 }}>변경 후</div>
          {renderRow('기간', updPeriod)}
          {renderRow('사유', upd.reason || '-')}
          {data.changeReason && (
            <>
              <div style={{ marginTop: 8, marginBottom: 4, fontWeight: 600, color: 'var(--color-gray-700)', fontSize: 13 }}>변경 요청 사유</div>
              {renderRow('', data.changeReason)}
            </>
          )}
        </div>
      );
    }

    default:
      return (
        <div style={{ fontSize: '13px', color: 'var(--color-gray-500)' }}>
          -
        </div>
      );
  }
}
